/* tsuinoScene.js — shared Three.js layer for the 終ノ空 redesign (V18).
 * mount(canvas, { mode, flesh, fps, pixelRatio, pauseWhenIdle })
 *   -> { setHighlight(i), setOpen(v | null), dispose() }
 * Everything is drawn with one stylised toon language (3 posterised bands,
 * dithered band edges, inverted-hull ink outline, crimson depth fog) so the
 * 3D props sit inside the flat CG paintings instead of on top of them.
 */
import * as T from 'three';

/* eslint-disable no-var */

var PAL = {
  ink: new T.Color('#0a0206'),
  shadow: new T.Color('#2a0710'),
  mid: new T.Color('#7a1220'),
  light: new T.Color('#d8414d'),
  hi: new T.Color('#f4cfc6'),
  fog: new T.Color('#3a0814'),
  rim: new T.Color('#ff6a5a')
};

var COMMON = [
  'uniform vec3 uInk; uniform vec3 uShadow; uniform vec3 uMid; uniform vec3 uLight; uniform vec3 uHi;',
  'uniform vec3 uFog; uniform vec3 uRim; uniform float uFogNear; uniform float uFogFar; uniform float uTime;',
  'float hash(vec2 p){ return fract(sin(dot(p, vec2(12.9898,78.233))) * 43758.5453); }',
  'vec3 toon(vec3 n, vec3 v, vec3 base){',
  '  vec3 key = normalize(vec3(0.35, 0.8, 0.3));',
  '  vec3 back = normalize(vec3(-0.2, 0.7, -0.7));',
  '  float d = dot(n, key) * 0.5 + 0.5;',
  '  d += (hash(floor(gl_FragCoord.xy / 2.0)) - 0.5) * 0.07;',
  '  vec3 c = d < 0.36 ? uShadow : (d < 0.76 ? base : uLight);',
  '  float fres = 1.0 - max(dot(n, v), 0.0);',
  '  float rim = smoothstep(0.55, 0.85, fres) * smoothstep(-0.2, 0.6, dot(n, back));',
  '  c = mix(c, uRim, rim * 0.85);',
  '  return c;',
  '}',
  'vec3 fogMix(vec3 c, float depth){ return mix(c, uFog, smoothstep(uFogNear, uFogFar, depth)); }'
].join('\n');

function baseUniforms(extra) {
  var u = {
    uInk: { value: PAL.ink }, uShadow: { value: PAL.shadow }, uMid: { value: PAL.mid },
    uLight: { value: PAL.light }, uHi: { value: PAL.hi }, uFog: { value: PAL.fog },
    uRim: { value: PAL.rim }, uFogNear: { value: 10 }, uFogFar: { value: 38 },
    uTime: { value: 0 }
  };
  for (var k in extra) u[k] = extra[k];
  return u;
}

/* ---------------- cross ---------------- */
var CROSS_VS = [
  'varying vec3 vN; varying vec3 vV; varying float vDepth; varying float vWY;',
  'uniform float uGrow;',
  'void main(){',
  '  vec3 p = position + normal * uGrow;',
  '  vec4 wp = modelMatrix * vec4(p, 1.0);',
  '  vec4 mv = viewMatrix * wp;',
  '  vN = normalize(normalMatrix * normal); vV = normalize(-mv.xyz);',
  '  vDepth = -mv.z; vWY = wp.y;',
  '  gl_Position = projectionMatrix * mv;',
  '}'
].join('\n');
var CROSS_FS = [
  COMMON,
  'varying vec3 vN; varying vec3 vV; varying float vDepth; varying float vWY;',
  'uniform float uFlat; uniform float uGround; uniform float uGlow; uniform float uOutline; uniform float uGate;',
  'void main(){',
  '  vec3 c = uOutline > 0.5 ? uInk : (uFlat > 0.5 ? uInk : toon(normalize(vN), normalize(vV), uMid));',
  '  if (uGate > 0.5 && uOutline < 0.5) c = mix(mix(uInk, uShadow, 0.6), c, uGlow);',
  '  else c = mix(c, uLight * 1.25, uGlow * (uOutline > 0.5 ? 0.6 : 0.5));',
  '  if (uGate > 0.5 && uOutline > 0.5) c = mix(uMid, uHi, uGlow);',
  '  c = fogMix(c, vDepth);',
  '  float a = smoothstep(uGround, uGround + 0.9, vWY);',
  '  gl_FragColor = vec4(c, a);',
  '}'
].join('\n');

function makeCross(opt) {
  var g = new T.Group();
  var s = opt.scale || 1;
  var parts = [[0.16, 2.4, 0.16, 0, 0], [1.25, 0.16, 0.16, 0, 0.55]];
  var mats = [];
  parts.forEach(function (d) {
    var geo = new T.BoxGeometry(d[0], d[1], d[2]);
    var u = baseUniforms({ uFlat: { value: opt.flat ? 1 : 0 }, uGround: { value: opt.ground != null ? opt.ground : -99 },
      uGlow: { value: 0 }, uOutline: { value: 0 }, uGrow: { value: 0 }, uGate: { value: opt.gate != null ? 1 : 0 } });
    var m = new T.ShaderMaterial({ uniforms: u, vertexShader: CROSS_VS, fragmentShader: CROSS_FS, transparent: true });
    var mesh = new T.Mesh(geo, m);
    mesh.position.set(d[3], d[4], 0);
    g.add(mesh); mats.push(m);
    if (!opt.flat) {
      var u2 = baseUniforms({ uFlat: { value: 1 }, uGround: u.uGround, uGlow: u.uGlow, uOutline: { value: 1 }, uGrow: { value: (opt.gate != null ? 0.05 : 0.035) / s }, uGate: u.uGate });
      var m2 = new T.ShaderMaterial({ uniforms: u2, vertexShader: CROSS_VS, fragmentShader: CROSS_FS, side: T.BackSide, transparent: true });
      var hull = new T.Mesh(geo, m2);
      hull.position.copy(mesh.position);
      g.add(hull); mats.push(m2);
    }
  });
  g.position.set(opt.pos[0], opt.pos[1], opt.pos[2]);
  g.rotation.set(0, opt.ry || 0, opt.tilt || 0);
  g.scale.setScalar(s);
  g.userData = { baseY: opt.pos[1], mats: mats, baseTilt: opt.tilt || 0, spin: opt.spin || 0, glow: 0, glowTarget: 0, sway: Math.random() * 6.28 };
  return g;
}

/* ---------------- tentacle ---------------- */
var TENT_VS = [
  'uniform float uTime; uniform float uLen; uniform float uAmp; uniform float uSpeed; uniform float uPhase;',
  'uniform vec2 uReach; uniform float uRecoil; uniform float uGrow; uniform float uR0;',
  'varying vec3 vN; varying vec3 vV; varying float vDepth; varying float vT; varying float vAng;',
  'vec3 bend(float t){',
  '  float tt = t * t;',
  '  float w = uTime * uSpeed + uPhase;',
  '  vec3 o;',
  '  o.x = sin(t * 3.1 + w) * uAmp * tt + uReach.x * tt * t + sin(t * 9.0 + w * 1.7) * 0.12 * tt * tt;',
  '  o.z = cos(t * 2.3 + w * 0.8) * uAmp * 0.7 * tt;',
  '  o.y = uReach.y * tt * t * 0.6;',
  '  o *= (1.0 - uRecoil * 0.6);',
  '  o.y -= uRecoil * t * uLen * 0.35;',
  '  return o;',
  '}',
  'void main(){',
  '  float t = clamp(position.y / uLen, 0.0, 1.0);',
  '  vT = t;',
  '  vec3 n0 = normalize(vec3(normal.x, 0.0, normal.z) + 1e-5);',
  '  vAng = atan(n0.z, n0.x);',
  '  vec3 o = bend(t);',
  '  vec3 o2 = bend(min(t + 0.01, 1.0));',
  '  vec3 tan = normalize(vec3(o2.x - o.x, uLen * 0.01, o2.z - o.z));',
  '  vec3 n = normalize(n0 - dot(n0, tan) * tan);',
  '  float r = mix(uR0, uR0 * 0.08, pow(t, 0.8));',
  '  vec3 p = vec3(n0.x * r, t * uLen, n0.z * r) + o + n * uGrow * (0.4 + r);',
  '  vec4 mv = modelViewMatrix * vec4(p, 1.0);',
  '  vN = normalize(normalMatrix * n); vV = normalize(-mv.xyz); vDepth = -mv.z;',
  '  gl_Position = projectionMatrix * mv;',
  '}'
].join('\n');
var TENT_FS = [
  COMMON,
  'uniform float uOutline;',
  'varying vec3 vN; varying vec3 vV; varying float vDepth; varying float vT; varying float vAng;',
  'void main(){',
  '  if (uOutline > 0.5) { gl_FragColor = vec4(fogMix(uInk, vDepth), 1.0); return; }',
  '  vec3 c = toon(normalize(vN), normalize(vV), uMid);',
  '  // suckers on the inner side',
  '  float side = abs(mod(vAng + 3.14159, 6.28318) - 3.14159);',
  '  float count = 26.0;',
  '  vec2 cell = vec2(side * 1.4, fract(vT * count) - 0.5);',
  '  float d = length(cell);',
  '  float rad = mix(0.34, 0.12, vT);',
  '  if (side < 0.9 && vT < 0.93) {',
  '    c = mix(c, uHi * 0.85, 1.0 - smoothstep(rad - 0.05, rad, d));',
  '    c = mix(c, uShadow, 1.0 - smoothstep(rad * 0.45 - 0.04, rad * 0.45, d));',
  '  }',
  '  // wet highlight streak',
  '  float spec = pow(max(dot(reflect(-normalize(vec3(0.35,0.8,0.3)), normalize(vN)), normalize(vV)), 0.0), 24.0);',
  '  c = mix(c, uHi, step(0.55, spec) * 0.9);',
  '  gl_FragColor = vec4(fogMix(c, vDepth), 1.0);',
  '}'
].join('\n');

function makeTentacle(opt) {
  var len = opt.len || 5, r0 = opt.r || 0.3;
  var geo = new T.CylinderGeometry(1, 1, len, 18, 120, true);
  geo.translate(0, len / 2, 0);
  var shared = {
    uLen: { value: len }, uAmp: { value: opt.amp != null ? opt.amp : 1.1 }, uSpeed: { value: opt.speed || 0.55 },
    uPhase: { value: opt.phase != null ? opt.phase : Math.random() * 6.28 }, uReach: { value: new T.Vector2() },
    uRecoil: { value: 0 }, uR0: { value: r0 }
  };
  var g = new T.Group();
  var body = new T.ShaderMaterial({ uniforms: baseUniforms(Object.assign({ uGrow: { value: 0 }, uOutline: { value: 0 } }, shared)),
    vertexShader: TENT_VS, fragmentShader: TENT_FS });
  var hull = new T.ShaderMaterial({ uniforms: baseUniforms(Object.assign({ uGrow: { value: 0.07 }, uOutline: { value: 1 } }, shared)),
    vertexShader: TENT_VS, fragmentShader: TENT_FS, side: T.BackSide });
  g.add(new T.Mesh(geo, body));
  g.add(new T.Mesh(geo, hull));
  g.children.forEach(function (m) { m.frustumCulled = false; });
  g.position.set(opt.pos[0], opt.pos[1], opt.pos[2]);
  g.rotation.set(opt.rx || 0, opt.ry || 0, opt.tilt || 0);
  g.userData = { mats: [body, hull], shared: shared, reachGain: opt.reach != null ? opt.reach : 1 };
  return g;
}

/* ---------------- eye ---------------- */
var EYE_VS = [
  'varying vec3 vN; varying vec3 vV; varying vec3 vP; varying float vDepth;',
  'uniform float uGrow;',
  'void main(){',
  '  vP = position;',
  '  vec4 mv = modelViewMatrix * vec4(position + normal * uGrow, 1.0);',
  '  vN = normalize(normalMatrix * normal); vV = normalize(-mv.xyz); vDepth = -mv.z;',
  '  gl_Position = projectionMatrix * mv;',
  '}'
].join('\n');
var BALL_FS = [
  COMMON,
  'varying vec3 vN; varying vec3 vV; varying vec3 vP; varying float vDepth;',
  'uniform float uDilate;',
  'void main(){',
  '  vec3 p = normalize(vP);',
  '  float ang = acos(clamp(p.z, -1.0, 1.0));',
  '  float irisA = 0.62;',
  '  float r = ang / irisA;',
  '  vec3 sclera = mix(uHi, vec3(0.93, 0.78, 0.74), smoothstep(0.2, 1.4, ang));',
  '  float th = atan(p.y, p.x);',
  '  float vein = smoothstep(0.93, 1.0, sin(th * 23.0 + sin(ang * 9.0) * 2.0)) * smoothstep(1.0, 1.6, ang);',
  '  sclera = mix(sclera, uLight, vein * 0.8);',
  '  vec3 base = sclera;',
  '  if (r < 1.0) {',
  '    float stri = 0.5 + 0.5 * sin(th * 60.0 + hash(vec2(floor(th * 18.0), 1.0)) * 6.0);',
  '    vec3 iris = mix(uShadow, uLight, smoothstep(0.35, 1.0, r) * 0.8 + stri * 0.25);',
  '    iris = mix(iris, uInk, smoothstep(0.86, 1.0, r));',
  '    base = iris;',
  '    if (r < uDilate) base = uInk;',
  '  }',
  '  vec3 n = normalize(vN), v = normalize(vV);',
  '  vec3 key = normalize(vec3(0.45, 0.55, 0.75));',
  '  float d = dot(n, key) * 0.5 + 0.5 + (hash(floor(gl_FragCoord.xy / 2.0)) - 0.5) * 0.1;',
  '  vec3 c = base * (d < 0.33 ? 0.45 : (d < 0.62 ? 0.78 : 1.0));',
  '  float h1 = dot(n, normalize(vec3(-0.35, 0.42, 1.0)));',
  '  float h2 = dot(n, normalize(vec3(-0.08, 0.18, 1.0)));',
  '  c = mix(c, vec3(1.0), step(0.992, h1));',
  '  c = mix(c, vec3(1.0), step(0.9985, h2));',
  '  gl_FragColor = vec4(fogMix(c, vDepth), 1.0);',
  '}'
].join('\n');
var LID_FS = [
  COMMON,
  'varying vec3 vN; varying vec3 vV; varying vec3 vP; varying float vDepth;',
  'uniform float uOpen; uniform float uOutline;',
  'void main(){',
  '  vec3 p = normalize(vP);',
  '  float w = sqrt(max(0.0, 1.0 - pow(p.x / 0.97, 2.0)));',
  '  float edge = uOpen * w;',
  '  float cy = p.y + 0.08 * (1.0 - w);',
  '  float inside = edge - abs(cy);',
  '  if (p.z > 0.0 && inside > 0.0) discard;',
  '  vec3 c;',
  '  if (uOutline > 0.5) c = uInk;',
  '  else {',
  '    c = toon(normalize(vN), normalize(vV), uMid * 0.8);',
  '    if (p.z > 0.0 && inside > -0.07) c = uInk;',
  '    float fold = smoothstep(0.02, 0.0, abs(abs(cy) - edge - 0.16)) * step(0.0, p.z);',
  '    c = mix(c, uShadow, fold);',
  '  }',
  '  gl_FragColor = vec4(fogMix(c, vDepth), 1.0);',
  '}'
].join('\n');

function makeEye(opt) {
  var g = new T.Group();
  var r = opt.r || 1;
  var ballU = baseUniforms({ uGrow: { value: 0 }, uDilate: { value: 0.42 } });
  var ball = new T.Mesh(new T.SphereGeometry(1, 64, 48), new T.ShaderMaterial({ uniforms: ballU, vertexShader: EYE_VS, fragmentShader: BALL_FS }));
  var openU = { value: opt.open != null ? opt.open : 0.55 };
  var lidU = baseUniforms({ uGrow: { value: 0 }, uOpen: openU, uOutline: { value: 0 } });
  var lidMat = opt.fleshShared
    ? new T.ShaderMaterial({ uniforms: Object.assign({ uOpen: openU, uOutline: { value: 0 } }, opt.fleshShared), vertexShader: fleshLidVS(), fragmentShader: fleshLidFS(), side: T.DoubleSide })
    : new T.ShaderMaterial({ uniforms: lidU, vertexShader: EYE_VS, fragmentShader: LID_FS, side: T.DoubleSide });
  var lid = new T.Mesh(new T.SphereGeometry(1.06, opt.fleshShared ? 128 : 64, opt.fleshShared ? 96 : 48), lidMat);
  var hullU = baseUniforms({ uGrow: { value: opt.fleshShared ? 0.1 : 0.05 }, uOpen: openU, uOutline: { value: 1 } });
  var hull = new T.Mesh(new T.SphereGeometry(1.06, 64, 48), new T.ShaderMaterial({ uniforms: hullU, vertexShader: EYE_VS, fragmentShader: LID_FS, side: T.BackSide }));
  g.add(ball, lid, hull);
  g.position.set(opt.pos[0], opt.pos[1], opt.pos[2]);
  g.scale.setScalar(r);
  g.userData = { ball: ball, open: openU, baseOpen: openU.value, mats: [ball.material, lid.material, hull.material], dilate: ballU.uDilate,
    nextBlink: 2 + Math.random() * 3, blinkT: -1, look: new T.Vector3(0, 0, 1) };
  return g;
}

/* ---------------- flesh corridor ---------------- */
// Procedural stand-in for the "flesh corridor" CG: a rounded-square tunnel
// whose walls are pushed inward by fbm noise, organ-like lumps and ribs,
// breathing on a double-thump heartbeat. Same toon/ink language as the props.
var SNOISE = [
  'vec3 mod289(vec3 x){ return x - floor(x * (1.0 / 289.0)) * 289.0; }',
  'vec4 mod289(vec4 x){ return x - floor(x * (1.0 / 289.0)) * 289.0; }',
  'vec4 permute(vec4 x){ return mod289(((x * 34.0) + 1.0) * x); }',
  'vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314 * r; }',
  'float snoise(vec3 v){',
  '  const vec2 C = vec2(1.0/6.0, 1.0/3.0); const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);',
  '  vec3 i = floor(v + dot(v, C.yyy)); vec3 x0 = v - i + dot(i, C.xxx);',
  '  vec3 g = step(x0.yzx, x0.xyz); vec3 l = 1.0 - g; vec3 i1 = min(g.xyz, l.zxy); vec3 i2 = max(g.xyz, l.zxy);',
  '  vec3 x1 = x0 - i1 + C.xxx; vec3 x2 = x0 - i2 + C.yyy; vec3 x3 = x0 - D.yyy;',
  '  i = mod289(i);',
  '  vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));',
  '  float n_ = 0.142857142857; vec3 ns = n_ * D.wyz - D.xzx;',
  '  vec4 j = p - 49.0 * floor(p * ns.z * ns.z); vec4 x_ = floor(j * ns.z); vec4 y_ = floor(j - 7.0 * x_);',
  '  vec4 x = x_ * ns.x + ns.yyyy; vec4 y = y_ * ns.x + ns.yyyy; vec4 h = 1.0 - abs(x) - abs(y);',
  '  vec4 b0 = vec4(x.xy, y.xy); vec4 b1 = vec4(x.zw, y.zw);',
  '  vec4 s0 = floor(b0) * 2.0 + 1.0; vec4 s1 = floor(b1) * 2.0 + 1.0; vec4 sh = -step(h, vec4(0.0));',
  '  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy; vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;',
  '  vec3 p0 = vec3(a0.xy, h.x); vec3 p1 = vec3(a0.zw, h.y); vec3 p2 = vec3(a1.xy, h.z); vec3 p3 = vec3(a1.zw, h.w);',
  '  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));',
  '  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;',
  '  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0); m = m * m;',
  '  return 42.0 * dot(m * m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));',
  '}',
  'float fbm(vec3 p){ float a = 0.5, s = 0.0; for (int i = 0; i < 3; i++) { s += a * snoise(p); p *= 2.03; a *= 0.5; } return s; }'
].join('\n');

// Corridor frame (matches the CG: flat wet floor, dark ceiling, lit door at the end)
var CORR = { hx: 6.4, top: 4.2, floor: -3.1, zNear: 12.0, zFar: -52.0 };
var DOOR = { x: 1.6, top: 0.8 };

/* Generic flesh surface, layered from several generators so no single
 * pattern dominates:
 *   L1  additively-weighted Voronoi, large cells  -> piled organs
 *   L2  additively-weighted Voronoi, small cells  -> nodules between them
 *   G   domain-warped ridged simplex               -> intestine coils draped on top
 *   F   fbm                                         -> skin detail (bump only)
 *   V   thin ridged lines                           -> vessels
 * Each cell draws its own tissue colour from a mixed palette. */
var FLESH_LIB = [
  SNOISE,
  'vec3 m289v3(vec3 x){ return x - floor(x * (1.0 / 289.0)) * 289.0; }',
  'vec2 m289v2(vec2 x){ return x - floor(x * (1.0 / 289.0)) * 289.0; }',
  'vec3 perm3(vec3 x){ return m289v3(((x * 34.0) + 1.0) * x); }',
  'float snoise2(vec2 v){',
  '  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);',
  '  vec2 i = floor(v + dot(v, C.yy)); vec2 x0 = v - i + dot(i, C.xx);',
  '  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);',
  '  vec4 x12 = x0.xyxy + C.xxzz; x12.xy -= i1; i = m289v2(i);',
  '  vec3 p = perm3(perm3(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));',
  '  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0); m = m * m; m = m * m;',
  '  vec3 x = 2.0 * fract(p * C.www) - 1.0; vec3 h = abs(x) - 0.5; vec3 ox = floor(x + 0.5); vec3 a0 = x - ox;',
  '  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);',
  '  vec3 g; g.x = a0.x * x0.x + h.x * x0.y; g.yz = a0.yz * x12.xz + h.yz * x12.yw;',
  '  return 130.0 * dot(m, g);',
  '}',
  'float h1(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }',
  'vec2 h2(vec2 p){ return fract(sin(vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)))) * 43758.5453); }',
  'float hash(vec2 p){ return fract(sin(dot(p, vec2(12.9898,78.233))) * 43758.5453); }',
  // weighted voronoi: x=F1 (radius-normalised), y=F2, z=cell hash, w=cell radius
  'vec4 voro(vec2 x, float seed){',
  '  vec2 n = floor(x), f = fract(x); float F1 = 9.0, F2 = 9.0, id = 0.0, rad = 1.0;',
  '  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {',
  '    vec2 g = vec2(float(i), float(j)); vec2 c = n + g + seed;',
  '    vec2 r = g + h2(c) * 0.9 - f;',
  '    float rr = 0.45 + 0.55 * h1(c + 7.3);',
  '    float d = length(r * vec2(1.0, 0.8 + 0.5 * h1(c + 2.1))) / rr;',
  '    if (d < F1) { F2 = F1; F1 = d; id = h1(c + 13.7); rad = rr; } else if (d < F2) { F2 = d; }',
  '  }',
  '  return vec4(F1, F2, id, rad);',
  '}',
  'float gutsField(vec2 w){',
  '  vec2 q = w * 0.62;',
  '  vec2 warp = vec2(snoise2(q * 0.55 + 11.0), snoise2(q * 0.55 + 23.0)) * 1.6;',
  '  return 1.0 - abs(snoise2(q + warp));',
  '}',
  // layered height. out: layer weights (x=L1, y=L2, z=guts) and cell ids
  'float fleshH(vec2 w, out vec3 wt, out vec4 A, out vec4 B, out float gut){',
  '  vec2 wp = vec2(snoise2(w * 0.23 + 1.3), snoise2(w * 0.23 + 8.1)) * 1.3 + vec2(snoise2(w * 0.7 + 4.4), snoise2(w * 0.7 + 9.9)) * 0.35;',
  '  A = voro(w * 0.42 + wp * 0.42, 0.0);',
  '  B = voro(w * 1.15 + 3.7 + wp * 0.8, 17.0);',
  '  float base = snoise2(w * 0.3) * 0.25 + snoise2(w * 0.9) * 0.12;',
  '  float a = pow(max(0.0, 1.0 - A.x * A.x), 0.7) * smoothstep(0.0, 0.3, A.y - A.x) * (0.6 + 0.8 * A.w) + base;',
  '  float b = pow(max(0.0, 1.0 - B.x * B.x), 0.6) * smoothstep(0.0, 0.2, B.y - B.x) * 0.35 + 0.1 + base * 0.6;',
  '  gut = gutsField(w);',
  '  float t = sqrt(clamp((gut - 0.8) / 0.2, 0.0, 1.0));',
  '  float gh = t * 0.38 + max(a, b) * 0.7;',
  '  float kk = 0.25; float hm = clamp(0.5 + 0.5 * (a - b) / kk, 0.0, 1.0);',
  '  float h = mix(b, a, hm) + kk * hm * (1.0 - hm);',
  '  wt = vec3(hm, 1.0 - hm, 0.0);',
  '  if (gh > h && t > 0.05) { float gm = smoothstep(0.0, 0.12, gh - h); h = mix(h, gh, gm); wt = mix(wt, vec3(0.0, 0.0, 1.0), gm); }',
  '  return h;',
  '}',
  'vec3 tissue(float k){',
  '  if (k < 0.34) return vec3(0.58, 0.1, 0.07);',
  '  if (k < 0.58) return vec3(0.78, 0.3, 0.2);',
  '  if (k < 0.7) return vec3(0.74, 0.44, 0.4);',
  '  if (k < 0.8) return vec3(0.5, 0.36, 0.42);',
  '  if (k < 0.94) return vec3(0.34, 0.06, 0.06);',
  '  return vec3(0.76, 0.6, 0.4);',
  '}',
  'uniform float uBeat;',
  'vec3 doorPos(){ return vec3(0.0, -1.5, ' + (CORR.zFar + 0.5).toFixed(1) + '); }'
].join('\n');

var FLESH_FRAG = [
  'vec3 perturbNormalArb(vec3 surf_pos, vec3 surf_norm, vec2 dHdxy){',
  '  vec3 sx = dFdx(surf_pos); vec3 sy = dFdy(surf_pos);',
  '  vec3 R1 = cross(sy, surf_norm); vec3 R2 = cross(surf_norm, sx);',
  '  float det = dot(sx, R1);',
  '  vec3 grad = sign(det) * (dHdxy.x * R1 + dHdxy.y * R2);',
  '  return normalize(abs(det) * surf_norm - grad);',
  '}',
  // shared lighting: key + door light, wrap diffuse, SSS rim, wet speculars, AO, depth fog
  'vec3 lightFlesh(vec3 W, vec3 n, vec3 base, float ao, float gloss, float ceilK){',
  '  vec3 v = normalize(cameraPosition - W);',
  '  vec3 lk = normalize(vec3(0.0, 2.0, 8.0) - W);',
  '  vec3 dp = doorPos() - W; float dd = length(dp); vec3 dl = dp / dd;',
  '  float doorI = 2.4 / (1.0 + dd * dd * 0.0022);',
  '  float keyI = 1.0 / (1.0 + pow(length(vec3(0.0, 2.0, 8.0) - W) * 0.055, 2.0));',
  '  float dif = max(dot(n, lk), 0.0) * 1.5 * keyI + max(dot(n, lk) * 0.5 + 0.5, 0.0) * 0.22 + max(dot(n, dl) * 0.7 + 0.3, 0.0) * doorI * 0.9;',
  '  dif += (hash(floor(gl_FragCoord.xy / 2.0)) - 0.5) * 0.04;',
  '  vec3 c = base * (0.05 + dif) * ao;',
  '  float fres = pow(1.0 - max(dot(n, v), 0.0), 3.0);',
  '  c += vec3(0.55, 0.06, 0.05) * fres * 0.45 * ao;',
  '  float s1 = pow(max(dot(n, normalize(lk + v)), 0.0), 70.0);',
  '  float s2 = pow(max(dot(n, normalize(dl + v)), 0.0), 45.0) * doorI;',
  '  float spec = (s1 + s2) * gloss;',
  '  c += vec3(1.0, 0.86, 0.8) * (smoothstep(0.35, 0.6, spec) * 0.7 + spec * 0.3) * ao;',
  '  c *= mix(1.0, 0.3, ceilK);',
  '  float depth = length(W - cameraPosition);',
  '  c = mix(c, vec3(0.05, 0.01, 0.015), smoothstep(30.0, 80.0, depth) * 0.55);',
  '  return c;',
  '}'
].join('\n');

var FLESH_VS = [
  'uniform float uTime;',
  'varying vec3 vW; varying vec3 vN; varying vec2 vWall; varying vec2 vSide;',
  FLESH_LIB,
  'vec3 wallPos(vec2 uv, out vec2 wall, out vec2 side, out float h){',
  '  float hx = ' + CORR.hx.toFixed(2) + ', fl = ' + CORR.floor.toFixed(2) + ', tp = ' + CORR.top.toFixed(2) + ';',
  '  float W = 2.0 * hx, H = tp - fl, P = 2.0 * (W + H);',
  '  float s = uv.x * P;',
  '  vec2 p; vec2 inw;',
  '  if (s < W) { p = vec2(-hx + s, fl); inw = vec2(0.0, 1.0); }',
  '  else if (s < W + H) { p = vec2(hx, fl + (s - W)); inw = vec2(-1.0, 0.0); }',
  '  else if (s < 2.0 * W + H) { p = vec2(hx - (s - W - H), tp); inw = vec2(0.0, -1.0); }',
  '  else { p = vec2(-hx, tp - (s - 2.0 * W - H)); inw = vec2(1.0, 0.0); }',
  '  float z = mix(' + CORR.zNear.toFixed(1) + ', ' + CORR.zFar.toFixed(1) + ', uv.y);',
  '  wall = vec2(s, z); side = inw;',
  '  vec3 wt; vec4 A; vec4 B; float g;',
  '  h = fleshH(wall, wt, A, B, g);',
  '  float k = inw.y > 0.5 ? 0.09 : (inw.y < -0.5 ? 0.85 : 1.0);',
  '  float pillar = pow(0.5 + 0.5 * cos(z * 6.2831853 / 13.0 + 0.8), 10.0) * (1.0 - step(0.5, inw.y));',
  '  h += pillar * (0.6 + 0.4 * snoise2(wall * 0.4));',
  '  h *= k * (1.0 - smoothstep(-60.0, -65.5, z) * 0.5);',
  '  return vec3(p + inw * h, z);',
  '}',
  'void main(){',
  '  vec2 w0; vec2 sd; float h; vec2 wa; vec2 sa; float ha; vec2 wb; vec2 sb; float hb;',
  '  vec3 p0 = wallPos(uv, w0, sd, h);',
  '  vec3 px = wallPos(uv + vec2(1.0 / 360.0, 0.0), wa, sa, ha);',
  '  vec3 py = wallPos(uv + vec2(0.0, 1.0 / 360.0), wb, sb, hb);',
  '  vec3 n = normalize(cross(px - p0, py - p0));',
  '  if (dot(n, vec3(sd, 0.0)) < 0.0) n = -n;',
  '  vN = n; vWall = w0; vSide = sd;',
  '  vec4 wp = modelMatrix * vec4(p0, 1.0); vW = wp.xyz;',
  '  gl_Position = projectionMatrix * viewMatrix * wp;',
  '}'
].join('\n');

var FLESH_FS = [
  'uniform float uTime;',
  'varying vec3 vW; varying vec3 vN; varying vec2 vWall; varying vec2 vSide;',
  FLESH_LIB,
  FLESH_FRAG,
  'void main(){',
  '  vec3 wt; vec4 A; vec4 B; float gut;',
  '  float h = fleshH(vWall, wt, A, B, gut);',
  // tissue colour: the winning layer picks it, each cell its own
  '  vec3 base = tissue(A.z) * wt.x + mix(tissue(B.z), tissue(A.z), 0.5) * wt.y;',
  '  base = mix(base, vec3(0.6, 0.14, 0.1), 0.35 * (0.5 + 0.5 * snoise2(vWall * 0.5 + 3.0)));',
  '  float gband = 0.5 + 0.5 * snoise2(vWall * 0.35 + 5.0);',
  '  vec3 gutC = mix(vec3(0.62, 0.5, 0.58), vec3(0.84, 0.56, 0.52), gband);',
  '  base = mix(base, gutC, wt.z);',
  // mottled skin + fine bump
  '  float fine = snoise2(vWall * 4.2) * 0.5 + snoise2(vWall * 9.5) * 0.25;',
  '  base *= 0.82 + 0.3 * (snoise2(vWall * 2.3 + 9.0) * 0.5 + 0.5);',
  '  float segs = wt.z * sin(dot(vWall, vec2(0.7, 0.7)) * 26.0 + gut * 30.0) * 0.5;',
  '  float bumpH = fine * 0.025 + segs * 0.04;',
  '  vec3 n = perturbNormalArb(vW, normalize(vN), vec2(dFdx(bumpH), dFdy(bumpH)));',
  // vessels over organs (dark), a few bright ones that pulse
  '  float vw = 1.0 - abs(snoise2(vWall * 1.9 + vec2(snoise2(vWall * 0.8), 0.0) * 0.8));',
  '  float vein = smoothstep(0.972, 0.994, vw) * (1.0 - wt.z) * 0.6;',
  '  float vw2 = 1.0 - abs(snoise2(vWall * 4.3 - 7.0));',
  '  float vein2 = smoothstep(0.985, 0.998, vw2) * (1.0 - wt.z) * 0.5;',
  '  base = mix(base, mix(vec3(0.3, 0.02, 0.05), vec3(0.95, 0.22, 0.18), uBeat * 0.7), max(vein, vein2));',
  // cavity AO: crevices between organs, under coils
  '  float creaseA = (1.0 - smoothstep(0.0, 0.22, A.y - A.x)) * 0.8;',
  '  float creaseB = (1.0 - smoothstep(0.0, 0.09, B.y - B.x)) * wt.y;',
  '  float gutEdge = smoothstep(0.74, 0.8, gut) * (1.0 - smoothstep(0.8, 0.84, gut));',
  '  float ao = mix(0.18, 1.0, smoothstep(-0.1, 0.8, h)) * (1.0 - creaseA * 0.85) * (1.0 - creaseB * 0.6) * (1.0 - gutEdge * 0.7);',
  '  float ceilK = step(0.5, -vSide.y);',
  '  float floorK = step(0.5, vSide.y);',
  '  base *= mix(1.0, 0.55, floorK);',
  '  ao = mix(ao, mix(0.6, 1.0, ao), floorK);',
  '  base = mix(base, vec3(0.42, 0.08, 0.07), floorK * 0.5);',
  '  float gloss = 0.75 + 0.4 * wt.z;',
  '  vec3 c = lightFlesh(vW, n, base, ao, gloss, ceilK);',
  // wet floor streaks catching the door light
  '  if (vSide.y > 0.5) {',
  '    float streak = smoothstep(0.35, 0.95, snoise2(vec2(vW.x * 0.3, vW.z * 1.4)));',
  '    float nearD = smoothstep(-10.0, -64.0, vW.z) * (1.0 - smoothstep(1.2, 4.5, abs(vW.x)));',
  '    c = mix(c, vec3(1.0, 0.6, 0.5), streak * nearD * 0.5);',
  '  }',
  '  gl_FragColor = vec4(c, 1.0);',
  '}'
].join('\n');

// end wall with the lit double door
var DOOR_FS = [
  'uniform float uTime;',
  'varying vec3 vW; varying vec2 vUv;',
  FLESH_LIB,
  'void main(){',
  '  vec2 w = vW.xy * 1.4;',
  '  float inDoor = step(abs(vW.x), ' + DOOR.x.toFixed(2) + ') * step(vW.y, ' + DOOR.top.toFixed(2) + ');',
  '  vec3 c;',
  '  float m = snoise2(w * 2.0) * 0.5 + snoise2(w * 6.0) * 0.3;',
  '  if (inDoor > 0.5) {',
  '    c = mix(vec3(0.95, 0.5, 0.45), vec3(1.0, 0.8, 0.72), clamp(0.5 + m * 0.6, 0.0, 1.0));',
  '    float vein = smoothstep(0.95, 0.99, 1.0 - abs(snoise2(w * 1.5 + 5.0)));',
  '    c = mix(c, vec3(0.7, 0.12, 0.1), vein * 0.8);',
  '    c = mix(c, vec3(0.25, 0.04, 0.05), 1.0 - smoothstep(0.02, 0.06, abs(vW.x)));',
  '    c *= 0.85 + uBeat * 0.2;',
  '  } else {',
  '    vec4 A = voro(w * 0.9, 3.0);',
  '    c = mix(tissue(A.z), vec3(0.5, 0.12, 0.1), 0.6) * (0.25 + 0.25 * sqrt(max(0.0, 1.0 - A.x * A.x)) + m * 0.1) * (1.0 - (1.0 - smoothstep(0.0, 0.2, A.y - A.x)) * 0.5);',
  '    c *= mix(1.0, 0.35, step(' + (CORR.top - 1.2).toFixed(2) + ', vW.y));',
  '    float frame = 1.0 - smoothstep(0.0, 0.45, max(abs(vW.x) - ' + DOOR.x.toFixed(2) + ', vW.y - ' + DOOR.top.toFixed(2) + '));',
  '    c = mix(c, vec3(1.0, 0.5, 0.4), frame * 0.7);',
  '  }',
  '  gl_FragColor = vec4(c, 1.0);',
  '}'
].join('\n');

var GLOW_FS = [
  'uniform float uBeat; varying vec2 vUv;',
  'void main(){',
  '  vec2 p = (vUv - vec2(0.5, 0.42)) * vec2(1.0, 1.3);',
  '  float g = exp(-dot(p, p) * 14.0);',
  '  vec3 c = vec3(0.9, 0.38, 0.3) * g * (0.5 + uBeat * 0.2);',
  '  gl_FragColor = vec4(c, g);',
  '}'
].join('\n');

// scattered props: intestine coils and organ blobs. One shader, instancing-aware.
var PROP_VS = [
  'uniform float uTime; uniform float uBulge; uniform float uLump; uniform float uBulgeF;',
  'varying vec3 vW; varying vec3 vN; varying vec3 vObj; varying float vSeed; varying float vU;',
  FLESH_LIB,
  'void main(){',
  '  vec3 pos = position; vec3 nrm = normal;',
  '  float seed = 0.0;',
  '#ifdef USE_INSTANCING',
  '  seed = fract(sin(dot(instanceMatrix[3].xyz, vec3(12.9898, 78.233, 37.719))) * 43758.5453);',
  '#endif',
  '  float lump = uLump * (snoise(pos * 1.7 + seed * 40.0) * 0.22 + snoise(pos * 4.5 + seed * 17.0) * 0.06);',
  '  float bul = uBulge * sin(uv.x * uBulgeF) * 0.035;',
  '  pos += nrm * (lump + bul + uBeat * 0.02 * uLump);',
  '  vec4 wp = vec4(pos, 1.0);',
  '#ifdef USE_INSTANCING',
  '  wp = instanceMatrix * wp; nrm = mat3(instanceMatrix) * nrm;',
  '#endif',
  '  wp = modelMatrix * wp;',
  '  vW = wp.xyz; vN = normalize(mat3(modelMatrix) * nrm); vObj = pos; vSeed = seed; vU = uv.x;',
  '  gl_Position = projectionMatrix * viewMatrix * wp;',
  '}'
].join('\n');
var PROP_FS = [
  'uniform float uTime; uniform vec3 uTint; uniform float uUseTint; uniform float uOutline;',
  'varying vec3 vW; varying vec3 vN; varying vec3 vObj; varying float vSeed; varying float vU;',
  FLESH_LIB,
  FLESH_FRAG,
  'void main(){',
  '  if (uOutline > 0.5) { gl_FragColor = vec4(0.035, 0.006, 0.015, 1.0); return; }',
  '  vec3 base = mix(tissue(vSeed), uTint, uUseTint);',
  '  base *= 0.8 + 0.35 * (snoise(vObj * 3.0 + vSeed * 9.0) * 0.5 + 0.5);',
  '  float vw = 1.0 - abs(snoise(vObj * 2.4 + vSeed * 5.0));',
  '  base = mix(base, mix(vec3(0.35, 0.03, 0.05), vec3(0.95, 0.22, 0.18), uBeat * 0.7), smoothstep(0.965, 0.99, vw) * (1.0 - uUseTint * 0.7));',
  '  float fine = snoise(vObj * 7.0) * 0.5;',
  '  vec3 n = perturbNormalArb(vW, normalize(vN), vec2(dFdx(fine), dFdy(fine)) * 0.05);',
  '  vec3 c = lightFlesh(vW, n, base, 1.0, 0.9, step(' + (CORR.top - 0.9).toFixed(2) + ', vW.y) * 0.6);',
  '  gl_FragColor = vec4(c, 1.0);',
  '}'
].join('\n');

function propMaterials(shared, opt) {
  var u = Object.assign({ uTint: { value: new T.Color(opt.tint || '#ffffff') }, uUseTint: { value: opt.tint ? 1 : 0 },
    uBulge: { value: opt.bulge || 0 }, uBulgeF: { value: opt.bulgeF || 60 }, uLump: { value: opt.lump || 0 }, uOutline: { value: 0 } }, shared);
  var body = new T.ShaderMaterial({ uniforms: u, vertexShader: PROP_VS, fragmentShader: PROP_FS });
  var hullU = Object.assign({}, u, { uOutline: { value: 1 } });
  var hull = new T.ShaderMaterial({ uniforms: hullU, fragmentShader: PROP_FS, side: T.BackSide,
    vertexShader: PROP_VS.replace('pos += nrm * (lump + bul + uBeat * 0.02 * uLump);', 'pos += nrm * (lump + bul + uBeat * 0.02 * uLump + 0.03);') });
  return [body, hull];
}

// deterministic RNG so every mount lays the corridor out the same way
function rng(seed) { return function () { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; }; }

// point on the corridor perimeter, pulled inward by `inset`
function wallPoint(side, a, z, inset) {
  var hx = CORR.hx, fl = CORR.floor, tp = CORR.top;
  if (side === 0) return new T.Vector3(-hx + inset, fl + a * (tp - fl), z);
  if (side === 1) return new T.Vector3(hx - inset, fl + a * (tp - fl), z);
  if (side === 2) return new T.Vector3(-hx + a * 2 * hx, fl + inset * 0.5, z);
  return new T.Vector3(-hx + a * 2 * hx, tp - inset, z);
}

function makeFlesh() {
  var g = new T.Group();
  var shared = { uTime: { value: 0 }, uBeat: { value: 0 } };
  var R = rng(19990720);
  var wall = new T.Mesh(new T.PlaneGeometry(1, 1, 360, 360),
    new T.ShaderMaterial({ uniforms: shared, vertexShader: FLESH_VS, fragmentShader: FLESH_FS, side: T.DoubleSide }));
  wall.frustumCulled = false;
  g.add(wall);

  var endVS = 'varying vec3 vW; varying vec2 vUv; void main(){ vUv = uv; vec4 wp = modelMatrix * vec4(position, 1.0); vW = wp.xyz; gl_Position = projectionMatrix * viewMatrix * wp; }';
  var end = new T.Mesh(new T.PlaneGeometry(CORR.hx * 2 + 2, CORR.top - CORR.floor + 2, 1, 1),
    new T.ShaderMaterial({ uniforms: shared, vertexShader: endVS, fragmentShader: DOOR_FS }));
  end.position.set(0, (CORR.top + CORR.floor) / 2, CORR.zFar);
  g.add(end);

  var glow = new T.Mesh(new T.PlaneGeometry(16, 11),
    new T.ShaderMaterial({ uniforms: shared, transparent: true, depthWrite: false, depthTest: false, blending: T.AdditiveBlending,
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: GLOW_FS }));
  glow.position.set(0, -1.0, CORR.zFar + 1.5);
  glow.renderOrder = 5;
  g.add(glow);

  var sway = [], beats = [];

  // 1) intestines slung across the ceiling
  [[-14, 1.4], [-25, 1.1], [-38, 0.9]].forEach(function (d, k) {
    var pts = [];
    for (var i = 0; i <= 48; i++) {
      var t = i / 48;
      var x = -CORR.hx + 0.3 + t * (CORR.hx * 2 - 0.6);
      pts.push(new T.Vector3(x, CORR.top - 0.3 - Math.sin(Math.PI * t) * d[1] + Math.sin(t * 9 + k) * 0.5, d[0] + Math.cos(t * 9 + k) * 0.7));
    }
    var geo = new T.TubeGeometry(new T.CatmullRomCurve3(pts), 400, 0.24 - k * 0.02, 14, false);
    var mats = propMaterials(shared, { tint: k % 2 ? '#a88fa0' : '#c48a80', bulge: 1, bulgeF: 70 });
    var gg = new T.Group(); gg.add(new T.Mesh(geo, mats[0]), new T.Mesh(geo, mats[1]));
    gg.userData.sway = k * 1.7; sway.push(gg); g.add(gg);
  });

  // 2) coils crawling over the walls: random walks hugging the wall, looping as they go
  for (var c = 0; c < 34; c++) {
    var side = c % 4 === 3 ? 2 : c % 2;
    var z0 = -2 - R() * 50, a0 = 0.1 + R() * 0.8;
    var inset = 0.7 + R() * 0.5;
    var pts = [], L = 10 + Math.floor(R() * 10), ph = R() * 6.28, loop = 0.25 + R() * 0.35;
    var da = (R() - 0.5) * 0.06, dz = (R() - 0.5) * 0.9;
    for (var i = 0; i < L; i++) {
      var p = wallPoint(side, Math.min(0.95, Math.max(0.03, a0 + da * i)), z0 + dz * i, inset);
      var ang = ph + i * 1.3;
      p.x += (side === 2 ? 1 : 0) * Math.cos(ang) * loop;
      p.y += (side === 2 ? 0 : 1) * Math.cos(ang) * loop;
      p.z += Math.sin(ang) * loop;
      if (side < 2) p.x += (side === 0 ? 1 : -1) * Math.sin(ang * 0.5) * 0.25;
      pts.push(p);
    }
    var geo = new T.TubeGeometry(new T.CatmullRomCurve3(pts), L * 14, 0.1 + R() * 0.1, 10, false);
    var tint = ['#b39aac', '#c9938a', '#d4a79c', '#9a7a8c'][c % 4];
    var mats = propMaterials(shared, { tint: tint, bulge: 1, bulgeF: L * 3 });
    g.add(new T.Mesh(geo, mats[0]), new T.Mesh(geo, mats[1]));
  }

  // 3) organ blobs half-sunk into the walls and floor (instanced, each lumped by its own seed)
  var N = 42;
  var bmats = propMaterials(shared, { lump: 1 });
  var bgeo = new T.IcosahedronGeometry(1, 4);
  var blobs = new T.InstancedMesh(bgeo, bmats[0], N);
  var bhull = new T.InstancedMesh(bgeo, bmats[1], N);
  var m = new T.Matrix4(), q = new T.Quaternion(), s = new T.Vector3(), e = new T.Euler();
  var base = [];
  for (var b = 0; b < N; b++) {
    var sd = b % 7 === 6 ? 2 : b % 2;
    var z = 2 - Math.pow(R(), 0.8) * 58;
    var r = 0.3 + R() * (b < 10 ? 0.7 : 0.4);
    var p2 = wallPoint(sd, 0.05 + R() * 0.9, z, r * 0.35);
    e.set(R() * 3, R() * 3, R() * 3); q.setFromEuler(e);
    s.set(r * (0.8 + R() * 0.5), r * (0.8 + R() * 0.6), r * (0.6 + R() * 0.4));
    m.compose(p2, q, s);
    blobs.setMatrixAt(b, m); bhull.setMatrixAt(b, m);
    base.push({ p: p2.clone(), q: q.clone(), s: s.clone(), beat: b < 3 });
  }
  blobs.frustumCulled = false; bhull.frustumCulled = false;
  g.add(blobs, bhull);
  beats.push({ meshes: [blobs, bhull], base: base });

  g.userData = { shared: shared, sway: sway, beats: beats };
  return g;
}


/* eyelid wrapped in the same visceral surface as the corridor (flesh scenes only) */
var LID_OPENING = [
  'float lidInside(vec3 p, float open){',
  '  float w = sqrt(max(0.0, 1.0 - pow(p.x / 0.97, 2.0)));',
  '  float cy = p.y + 0.08 * (1.0 - w);',
  '  return open * w - abs(cy);',
  '}'
].join('\n');
function fleshLidVS() {
  return [
    'uniform float uTime; uniform float uOpen;',
    'varying vec3 vW; varying vec3 vN; varying vec3 vP;',
    FLESH_LIB, LID_OPENING,
    'void main(){',
    '  vec3 p = normalize(position);',
    '  float away = smoothstep(0.02, 0.22, -lidInside(p, max(uOpen, 0.3))) + step(p.z, 0.0);',
    '  float lump = (snoise(p * 2.3 + 4.0) * 0.07 + snoise(p * 5.5) * 0.025 + uBeat * 0.02) * clamp(away, 0.0, 1.0);',
    '  vec3 pos = position + normal * lump;',
    '  vP = position;',
    '  vec4 wp = modelMatrix * vec4(pos, 1.0); vW = wp.xyz;',
    '  vN = normalize(mat3(modelMatrix) * normal);',
    '  gl_Position = projectionMatrix * viewMatrix * wp;',
    '}'
  ].join('\n');
}
function fleshLidFS() {
  return [
    'uniform float uTime; uniform float uOpen; uniform float uOutline;',
    'varying vec3 vW; varying vec3 vN; varying vec3 vP;',
    FLESH_LIB, FLESH_FRAG, LID_OPENING,
    'void main(){',
    '  vec3 p = normalize(vP);',
    '  float inside = lidInside(p, uOpen);',
    '  if (p.z > 0.0 && inside > 0.0) discard;',
    '  vec3 q = p * 2.6;',
    '  float k = snoise(q * 0.8 + 3.0) * 0.5 + 0.5;',
    '  vec3 base = mix(tissue(k), vec3(0.6, 0.13, 0.09), 0.4);',
    '  float gut = 1.0 - abs(snoise(q * 0.9 + vec3(snoise(q * 0.5), 0.0, 0.0) * 1.2));',
    '  base = mix(base, vec3(0.66, 0.52, 0.58), smoothstep(0.84, 0.92, gut) * 0.7);',
    '  base *= 0.82 + 0.3 * (snoise(q * 3.0) * 0.5 + 0.5);',
    '  float vein = smoothstep(0.955, 0.99, 1.0 - abs(snoise(q * 1.6 + 7.0)));',
    '  float vein2 = smoothstep(0.975, 0.996, 1.0 - abs(snoise(q * 3.8 - 2.0))) * 0.6;',
    '  base = mix(base, mix(vec3(0.32, 0.02, 0.05), vec3(0.95, 0.22, 0.18), uBeat * 0.7), max(vein, vein2));',
    '  float fine = snoise(q * 6.0) * 0.5 + snoise(q * 13.0) * 0.25;',
    '  vec3 n = perturbNormalArb(vW, normalize(vN), vec2(dFdx(fine), dFdy(fine)) * 0.03);',
    '  float crease = smoothstep(0.8, 0.84, gut) * (1.0 - smoothstep(0.84, 0.88, gut));',
    '  vec3 c = lightFlesh(vW, n, base, 1.0 - crease * 0.6, 0.95, 0.0);',
    // wet, dark rim where the lid meets the eyeball
    '  if (p.z > 0.0 && inside > -0.08) c = mix(vec3(0.04, 0.007, 0.015), vec3(0.35, 0.05, 0.05), smoothstep(-0.02, -0.08, inside));',
    '  gl_FragColor = vec4(c, 1.0);',
    '}'
  ].join('\n');
}

/* ---------------- scene presets ---------------- */
function preset(mode, aspect) {
  var P = { camZ: 10, fov: 35, eyes: [], crosses: [], tentacles: [], fog: [10, 38] };
  var narrow = aspect < 0.8;
  if (mode === 'hero') {
    if (narrow) {
      P.eyes.push({ pos: [0.7, 3.5, -8], r: 0.8, open: 0.5 });
      P.tentacles.push({ pos: [-2.6, -4.9, -1], len: 4.6, r: 0.26, tilt: -0.25 },
        { pos: [2.7, -5.0, -1.5], len: 5.2, r: 0.3, tilt: 0.3 });
      P.crosses.push({ pos: [-1.1, -1.8, -14], scale: 1.3, tilt: -0.12, flat: true, ground: -3.2 },
        { pos: [1.6, -2.1, -9], scale: 0.9, tilt: 0.1, ground: -3.0 });
    } else {
      P.eyes.push({ pos: [4.4, 2.7, -8], r: 1.0, open: 0.5 });
      P.tentacles.push(
        { pos: [-6.9, -4.3, -2], len: 5.6, r: 0.36, tilt: -0.42 },
        { pos: [-5.4, -4.8, -4.5], len: 4.2, r: 0.24, tilt: -0.2, amp: 1.4 },
        { pos: [6.8, -4.4, -2], len: 6.0, r: 0.38, tilt: 0.45 },
        { pos: [5.3, -4.9, -5], len: 4.0, r: 0.22, tilt: 0.15, amp: 1.5 });
      P.crosses.push(
        { pos: [-3.6, -2.9, -9], scale: 1.0, tilt: -0.18, ground: -3.9 },
        { pos: [2.6, -3.3, -12], scale: 0.9, tilt: 0.06, ground: -4.2 },
        { pos: [-8.5, -3.2, -22], scale: 1.3, tilt: 0.1, flat: true, ground: -4.8 },
        { pos: [9.5, -3.6, -26], scale: 1.4, tilt: -0.2, flat: true, ground: -5.2 },
        { pos: [0.4, -3.8, -30], scale: 1.1, tilt: 0.0, flat: true, ground: -5.6 });
    }
  } else if (mode === 'eye') {
    if (narrow) {
      P.eyes.push({ pos: [0, 0.9, -2], r: 1.7, open: 0.6 });
      P.tentacles.push({ pos: [-2.4, -5.0, -0.5], len: 4.4, r: 0.28, tilt: -0.1 }, { pos: [2.6, -5.2, -1], len: 5, r: 0.3, tilt: 0.2 });
    } else {
      P.eyes.push({ pos: [2.2, 0.3, -2], r: 2.7, open: 0.6 });
      P.tentacles.push(
        { pos: [7.2, -4.6, -3], len: 6.5, r: 0.42, tilt: 0.55 },
        { pos: [-1.8, -5.2, -3.5], len: 4.4, r: 0.28, tilt: 0.35, amp: 0.9 },
        { pos: [5.6, 4.8, -4], len: 5.4, r: 0.34, tilt: 2.6, amp: 1.2 });
    }
  } else if (mode === 'poem') {
    var n = narrow ? 5 : 11;
    for (var i = 0; i < n; i++) {
      var f = i / (n - 1);
      var x = narrow ? -2.4 + f * 4.8 : -9 + f * 18;
      var z = -1.5 - ((i * 7) % 5);
      P.tentacles.push({ pos: [x + Math.sin(i * 3.1) * 0.6, -4.8 + (z + 1.5) * 0.25, z], len: 2.6 + ((i * 5) % 4) * 0.9,
        r: 0.14 + ((i * 3) % 4) * 0.035, tilt: (x > 0 ? 0.08 : -0.08), amp: 1.2, speed: 0.35, reach: 0.4 });
    }
    P.fog = [8, 22];
  } else if (mode === 'tractatus') {
    P.crosses.push({ pos: narrow ? [0, 0.6, 0] : [-3.4, -0.8, 0], scale: narrow ? 1.0 : 1.35, tilt: 0, spin: 0.18 });
    P.crosses.push({ pos: [5, -1.9, -18], scale: 1.4, tilt: 0.12, flat: true, ground: -4.0 },
      { pos: [9, -2.2, -24], scale: 1.6, tilt: -0.1, flat: true, ground: -4.6 });
  } else if (mode === 'gates') {
    var gx = narrow ? [-1.35, -0.45, 0.45, 1.35] : [-4.8, -1.6, 1.6, 4.8];
    gx.forEach(function (x, i) {
      P.crosses.push({ pos: [x, narrow ? 2.4 : 1.2, -3], scale: narrow ? 0.32 : 0.7, tilt: [-0.08, 0.04, -0.03, 0.09][i], gate: i });
    });
    P.eyes.push({ pos: [0, narrow ? 4.8 : 3.3, -10], r: 0.75, open: 0.3 });
  } else if (mode === 'watcher') {
    P.eyes.push({ pos: narrow ? [0, 2.9, -4] : [0, 2.65, -4], r: narrow ? 0.7 : 0.9, open: 0.5 });
    P.tentacles.push(
      { pos: narrow ? [-2.3, -5.2, -1] : [-7.2, -4.6, -2], len: narrow ? 3.8 : 5.4, r: 0.32, tilt: -0.4, reach: 0.6 },
      { pos: narrow ? [2.4, -5.3, -1.5] : [7.4, -4.7, -2.5], len: narrow ? 4.2 : 5.8, r: 0.34, tilt: 0.42, reach: 0.6 });
    if (!narrow) P.crosses.push({ pos: [-9, -3.4, -22], scale: 1.3, tilt: 0.1, flat: true, ground: -5 },
      { pos: [10, -3.6, -26], scale: 1.5, tilt: -0.14, flat: true, ground: -5.4 });
  } else if (mode === 'strip') {
    P.eyes.push({ pos: [0, 0.35, -4], r: 1.0, open: 0.34 });
    [-14, -9.5, -5.5, 5.8, 10, 14.5].forEach(function (x, i) {
      P.crosses.push({ pos: [x, -1.7 - (i % 2) * 0.2, -14 - (i % 3) * 4], scale: 0.9 + (i % 3) * 0.2,
        tilt: [0.1, -0.08, 0.05, -0.14, 0.07, -0.03][i], flat: true, ground: -3.2 });
    });
    P.tentacles.push({ pos: [-12.5, -5.2, -3], len: 3.6, r: 0.3, tilt: -0.3, amp: 0.9 },
      { pos: [12.8, -5.4, -3], len: 3.9, r: 0.32, tilt: 0.35, amp: 0.9 });
    P.fog = [8, 30];
  }
  return P;
}

function mount(canvas, opts) {
  opts = opts || {};
  // Performance guards: 30 fps cap, pixel ratio 1, stop while the page is hidden,
  // and (unless opts.pauseWhenIdle === false) freeze on a still frame when the
  // pointer has not moved over this page for 1.5 s.
  var FPS = opts.fps || 30, IDLE_MS = 1500;
  var pauseIdle = opts.pauseWhenIdle !== false;
  var lastInput = -1e9, lastDraw = 0, drawn = 0, dirty = true;
  var mode = opts.mode || 'hero';
  var renderer = new T.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, premultipliedAlpha: false });
  renderer.setClearColor(0x000000, 0);
  var scene = new T.Scene();
  var camera = new T.PerspectiveCamera(35, 1, 0.1, 100);
  var built = null;
  var eyes = [], crosses = [], tents = [], flesh = null;
  var mouse = new T.Vector2(0, 0), mouseS = new T.Vector2(0, 0);
  var recoil = 0, highlight = -1, openOverride = null;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function build(aspect) {
    var narrow = aspect < 0.8;
    var key = narrow ? 'n' : 'w';
    if (built === key) return;
    built = key;
    while (scene.children.length) scene.remove(scene.children[0]);
    eyes = []; crosses = []; tents = []; flesh = null;
    var P = preset(mode, aspect);
    if (opts.flesh) { flesh = makeFlesh(); scene.add(flesh); P.crosses = []; }
    camera.fov = P.fov; camera.position.set(0, 0, P.camZ); camera.lookAt(0, 0, 0);
    P.eyes.forEach(function (o) { if (flesh) o.fleshShared = flesh.userData.shared; var e = makeEye(o); eyes.push(e); scene.add(e); });
    P.crosses.forEach(function (o) { var c = makeCross(o); c.userData.gate = o.gate; crosses.push(c); scene.add(c); });
    P.tentacles.forEach(function (o) { var t = makeTentacle(o); tents.push(t); scene.add(t); });
    scene.traverse(function (o) {
      if (o.material && o.material.uniforms && o.material.uniforms.uFogNear) {
        o.material.uniforms.uFogNear.value = P.fog[0]; o.material.uniforms.uFogFar.value = P.fog[1];
      }
    });
  }

  function resize() {
    var w = canvas.clientWidth || canvas.parentNode.clientWidth, h = canvas.clientHeight || canvas.parentNode.clientHeight;
    if (!w || !h) return;
    renderer.setPixelRatio(opts.pixelRatio || 1);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    build(camera.aspect);
    dirty = true;
    camera.updateProjectionMatrix();
  }

  function onMove(e) {
    var r = canvas.getBoundingClientRect();
    if (!r.width) return;
    mouse.set(((e.clientX - r.left) / r.width) * 2 - 1, -(((e.clientY - r.top) / r.height) * 2 - 1));
    mouse.x = Math.max(-1.5, Math.min(1.5, mouse.x)); mouse.y = Math.max(-1.5, Math.min(1.5, mouse.y));
    lastInput = performance.now();
  }
  function onDown(e) {
    var r = canvas.getBoundingClientRect();
    if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) recoil = 1;
    lastInput = performance.now();
  }
  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('pointerdown', onDown, { passive: true });

  var ro = new ResizeObserver(resize); ro.observe(canvas);
  var visible = true;
  var io = new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }, { threshold: 0 });
  io.observe(canvas);
  resize();

  var clock = new T.Clock(), t = 0, raf = 0;
  var tmpV = new T.Vector3(), tmpM = new T.Matrix4();
  function frame() {
    raf = requestAnimationFrame(frame);
    var now = performance.now();
    if (!visible || document.hidden) { clock.getDelta(); return; }
    var active = !pauseIdle || now - lastInput < IDLE_MS || drawn < 3;
    if (!active && !dirty) { clock.getDelta(); return; }
    if (now - lastDraw < 1000 / FPS - 2) return;
    lastDraw = now; drawn++; dirty = false;
    var dt = Math.min(clock.getDelta(), 0.05);
    if (!reduced) t += dt;
    mouseS.lerp(mouse, 1 - Math.pow(0.02, dt));
    recoil = Math.max(0, recoil - dt * 1.4);
    if (flesh) {
      var ph = t % 1.3;
      var beat = Math.exp(-ph * 9) + (ph > 0.28 ? 0.6 * Math.exp(-(ph - 0.28) * 9) : 0);
      flesh.userData.shared.uTime.value = t;
      flesh.userData.shared.uBeat.value = Math.min(1, beat + recoil * 0.8);
      camera.position.x = mouseS.x * 0.45; camera.position.y = mouseS.y * 0.3;
      camera.lookAt(0, 0.2, -12);
      flesh.userData.sway.forEach(function (o) { o.rotation.x = Math.sin(t * 0.5 + o.userData.sway) * 0.012; });
      flesh.userData.beats.forEach(function (bt) {
        bt.base.forEach(function (b, i) {
          if (!b.beat) return;
          tmpM.compose(b.p, b.q, b.s.clone().multiplyScalar(1 + beat * 0.06));
          bt.meshes.forEach(function (mm) { mm.setMatrixAt(i, tmpM); mm.instanceMatrix.needsUpdate = true; });
        });
      });
    }

    // world-space pointer on the z=-2 plane
    tmpV.set(mouseS.x, mouseS.y, 0.5).unproject(camera).sub(camera.position).normalize();
    var k = (-2 - camera.position.z) / tmpV.z;
    var pw = camera.position.clone().add(tmpV.multiplyScalar(k));

    eyes.forEach(function (e) {
      var u = e.userData;
      var target = new T.Vector3(pw.x, pw.y, camera.position.z + 2);
      var local = target.clone().sub(e.position).normalize();
      local.x = Math.max(-0.75, Math.min(0.75, local.x)); local.y = Math.max(-0.6, Math.min(0.6, local.y));
      local.z = Math.max(0.35, local.z); local.normalize();
      u.look.lerp(local, 1 - Math.pow(0.004, dt));
      tmpM.lookAt(new T.Vector3(), u.look, new T.Vector3(0, 1, 0));
      u.ball.quaternion.setFromRotationMatrix(tmpM);
      u.ball.rotateY(Math.PI);
      u.nextBlink -= dt;
      if (u.nextBlink < 0 && u.blinkT < 0) { u.blinkT = 0; u.nextBlink = 2.5 + Math.random() * 4; }
      var open = (openOverride != null ? openOverride : u.baseOpen) * (1 - recoil * 0.9);
      u.openS = u.openS == null ? open : u.openS + (open - u.openS) * (1 - Math.pow(0.002, dt)); open = u.openS;
      if (u.blinkT >= 0) { u.blinkT += dt; var b = u.blinkT / 0.22; open *= b < 1 ? Math.abs(1 - b * 2) : 1; if (b >= 1) u.blinkT = -1; }
      u.open.value = open;
      u.dilate.value = 0.42 + Math.sin(t * 0.7) * 0.05 + recoil * 0.25;
    });

    crosses.forEach(function (c) {
      var u = c.userData;
      if (u.spin) c.rotation.y = t * u.spin;
      c.rotation.z = u.baseTilt + Math.sin(t * 0.4 + u.sway) * 0.012;
      u.glowTarget = (u.gate != null && u.gate === highlight) ? 1 : 0;
      u.glow += (u.glowTarget - u.glow) * (1 - Math.pow(0.001, dt));
      u.mats.forEach(function (m) { m.uniforms.uTime.value = t; m.uniforms.uGlow.value = u.glow; });
      if (u.gate != null) c.position.y = u.baseY + u.glow * 0.25 + Math.sin(t * 0.8 + u.sway) * 0.04;
    });

    tents.forEach(function (g) {
      var u = g.userData;
      var dx = (pw.x - g.position.x), dy = (pw.y - g.position.y);
      u.shared.uReach.value.set(
        u.shared.uReach.value.x + (Math.max(-2.2, Math.min(2.2, dx * 0.25)) * u.reachGain - u.shared.uReach.value.x) * (1 - Math.pow(0.08, dt)),
        u.shared.uReach.value.y + (Math.max(-1, Math.min(1.2, dy * 0.12)) * u.reachGain - u.shared.uReach.value.y) * (1 - Math.pow(0.08, dt)));
      u.shared.uRecoil.value = recoil * recoil;
      u.mats.forEach(function (m) { m.uniforms.uTime.value = t; });
    });

    renderer.render(scene, camera);
  }
  raf = requestAnimationFrame(frame);

  return {
    setHighlight: function (i) { highlight = i; lastInput = performance.now() + 1500; },
    setOpen: function (v) { openOverride = v; lastInput = performance.now() + 1500; },
    dispose: function () {
      cancelAnimationFrame(raf); ro.disconnect(); io.disconnect();
      window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerdown', onDown);
      scene.traverse(function (o) {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose();
      });
      renderer.dispose();
      renderer.forceContextLoss();
    }
  };
}

export { mount };
